import { useMyAccountQuery } from '@kijiji/generated/graphql-types'
import { useRouter } from 'next/router'
import { useSession } from 'next-auth/react'
import { useTranslation } from 'next-i18next'
import { type ReactNode, useMemo } from 'react'
import { useTheme } from 'styled-components'

import { BRPCarouselLayout } from '@/components/brp/BRPCarouselLayout'
import { MaintainProtectTile } from '@/components/brp/MaintainProtectTile'
import {
  MaintainProtectCarouselWrapper,
  MaintainProtectStacksWrapper,
} from '@/components/brp/styled'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { isUserAuthenticated } from '@/features/auth/constants/user'
import { getMaintainAndProtectData } from '@/features/search/utils/vertical-brp/getMaintainAndProtectData'
import { useLocale } from '@/hooks/useLocale'
import { RemoteConfigs } from '@/lib/firebase/config/configs'
import { useConfig } from '@/lib/firebase/hooks'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { HeadlineText } from '@/ui/atoms/headline-text'
import { Spacing } from '@/ui/atoms/spacing'

export const MaintainProtectCarousel = () => {
  const { query } = useRouter()
  const { t } = useTranslation([TRANSLATION_KEYS.SRP])
  const { routeLocale } = useLocale()
  const { data: autocanData } = useConfig(RemoteConfigs.AUTOCAN.key) ?? {}
  const { status } = useSession()
  const { data: profile } = useMyAccountQuery({ skip: !isUserAuthenticated(status) })
  const profileData = profile?.myAccount || undefined
  const { spacing } = useTheme()

  const data = useMemo(
    () =>
      getMaintainAndProtectData(
        t,
        query,
        routeLocale,
        autocanData as { warrantyUrl: string },
        profileData
      ),
    [t, query, routeLocale, autocanData, profileData]
  )

  const handleTileClick = (tileLabel: string) => {
    trackEvent({
      action: GA_EVENT.BRPCarouselClick,
      label: `page=brp_landing_page;section=maintain_and_protect;tile=${tileLabel}`,
    })
  }

  const slides: ReactNode[] = data.map((item) => {
    const { label, subLabel, url, imgSrc, isExternalUrl } = item

    return (
      <MaintainProtectTile
        key={label}
        label={label}
        subLabel={subLabel}
        url={url}
        isExternalLink={isExternalUrl}
        imageSrc={imgSrc}
        onClick={handleTileClick}
      />
    )
  })

  const title = t('srp:maintain_protect.carousel.title')

  return (
    <>
      <MaintainProtectCarouselWrapper>
        <BRPCarouselLayout
          title={title}
          carouselName="maintain-and-protect"
          slides={slides}
          slidesToShow={{ small: 1, medium: 2, large: 4 }}
          slideCustomStyle="max-width: calc(30.6rem)"
          shouldShowArrows={false}
        />
      </MaintainProtectCarouselWrapper>
      <MaintainProtectStacksWrapper>
        <Spacing mBottom={spacing.defaultSmall}>
          <Spacing mBottom={spacing.large}>
            <HeadlineText as="h3" size="xLarge">
              {title}
            </HeadlineText>
          </Spacing>
          {slides.map((slide, i) => (
            <Spacing key={i} mBottom="1.4rem">
              {slide}
            </Spacing>
          ))}
        </Spacing>
      </MaintainProtectStacksWrapper>
    </>
  )
}
